import React, { useState } from 'react';

interface Client {
  id: number;
  name: string;
  type: 'residential' | 'commercial';
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  totalJobs: number;
  totalRevenue: string;
  lastService: string;
  status: 'active' | 'inactive';
  notes: string;
}

const ClientsPage: React.FC = () => {
  const [clients] = useState<Client[]>([
    {
      id: 1,
      name: 'John Smith',
      type: 'residential',
      email: 'john.smith@email.com',
      phone: '(555) 123-4567',
      address: '123 Main St',
      city: 'Anytown',
      state: 'CA',
      zipCode: '12345',
      totalJobs: 5,
      totalRevenue: '$3,250',
      lastService: '2024-01-10',
      status: 'active',
      notes: 'Prefers morning appointments. Has two AC units.'
    },
    {
      id: 2,
      name: 'ABC Corporation',
      type: 'commercial',
      email: 'facilities@abccorp.com',
      phone: '(555) 987-6543',
      address: '789 Business Blvd',
      city: 'Downtown',
      state: 'CA',
      zipCode: '54321',
      totalJobs: 12,
      totalRevenue: '$18,500',
      lastService: '2024-01-05',
      status: 'active',
      notes: 'Large office building. Quarterly maintenance contract.'
    },
    {
      id: 3,
      name: 'Sarah Johnson',
      type: 'residential',
      email: 'sarah@home.com',
      phone: '(555) 456-7890',
      address: '456 Oak Ave',
      city: 'Suburbs',
      state: 'CA',
      zipCode: '67890',
      totalJobs: 3,
      totalRevenue: '$1,850',
      lastService: '2023-12-15',
      status: 'active',
      notes: 'New heat pump installed last year.'
    },
    {
      id: 4,
      name: 'Mike Davis',
      type: 'residential',
      email: 'mike.davis@email.com',
      phone: '(555) 321-0987',
      address: '321 Pine Rd',
      city: 'Neighborhood',
      state: 'CA',
      zipCode: '09876',
      totalJobs: 1,
      totalRevenue: '$350',
      lastService: '2023-11-20',
      status: 'inactive',
      notes: 'Single service call for duct cleaning.'
    },
    {
      id: 5,
      name: 'XYZ Restaurant',
      type: 'commercial',
      email: 'manager@xyzrestaurant.com',
      phone: '(555) 654-3210',
      address: '654 Food St',
      city: 'Restaurant Row',
      state: 'CA',
      zipCode: '13579',
      totalJobs: 8,
      totalRevenue: '$12,400',
      lastService: '2024-01-08',
      status: 'active',
      notes: 'Kitchen exhaust system requires monthly maintenance.'
    }
  ]);

  const [showAddForm, setShowAddForm] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const filteredClients = clients.filter(client => {
    const typeMatch = filterType === 'all' || client.type === filterType;
    const statusMatch = filterStatus === 'all' || client.status === filterStatus;
    return typeMatch && statusMatch;
  });

  const getTotalRevenue = () => {
    return filteredClients.reduce((total, client) => {
      return total + parseFloat(client.totalRevenue.replace('$', '').replace(',', ''));
    }, 0);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ color: '#2c3e50', marginBottom: '10px' }}>Clients</h1>
          <p style={{ color: '#6c757d' }}>Manage your customer database</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          + Add New Client
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)' }}>
          <div className="stat-number">{filteredClients.length}</div>
          <div className="stat-label">Total Clients</div>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)' }}>
          <div className="stat-number">${getTotalRevenue().toLocaleString()}</div>
          <div className="stat-label">Total Revenue</div>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)' }}>
          <div className="stat-number">{filteredClients.filter(c => c.status === 'active').length}</div>
          <div className="stat-label">Active Clients</div>
        </div>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)' }}>
          <div className="stat-number">{filteredClients.filter(c => c.type === 'commercial').length}</div>
          <div className="stat-label">Commercial</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Type:</label>
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Types</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>
          <div>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Status:</label>
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {showAddForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Add New Client</h3>
          <form style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div className="form-group">
              <label>Name/Company *</label>
              <input type="text" required placeholder="Client name" />
            </div>
            <div className="form-group">
              <label>Type *</label>
              <select required>
                <option value="">Select Type</option>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" required placeholder="client@email.com" />
            </div>
            <div className="form-group">
              <label>Phone *</label>
              <input type="tel" required placeholder="(555) 123-4567" />
            </div>
            <div className="form-group">
              <label>Address *</label>
              <input type="text" required placeholder="Street address" />
            </div>
            <div className="form-group">
              <label>City *</label>
              <input type="text" required placeholder="City" />
            </div>
            <div className="form-group">
              <label>State *</label>
              <input type="text" required placeholder="State" maxLength={2} />
            </div>
            <div className="form-group">
              <label>ZIP Code *</label>
              <input type="text" required placeholder="12345" />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <textarea 
                rows={3} 
                placeholder="Additional notes about the client"
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  resize: 'vertical'
                }}
              ></textarea>
            </div>
          </form>
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button className="btn btn-primary">Save Client</button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Contact Info</th>
              <th>Address</th>
              <th>Type</th>
              <th>Jobs</th>
              <th>Revenue</th>
              <th>Last Service</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.map((client) => (
              <tr key={client.id}>
                <td>
                  <div>
                    <strong>{client.name}</strong>
                    <br />
                    <small style={{ color: '#6c757d' }}>ID: {client.id}</small>
                  </div>
                </td>
                <td>
                  <div>
                    {client.email}
                    <br />
                    <small style={{ color: '#6c757d' }}>{client.phone}</small>
                  </div>
                </td>
                <td>
                  <div>
                    {client.address}
                    <br />
                    <small style={{ color: '#6c757d' }}>
                      {client.city}, {client.state} {client.zipCode}
                    </small>
                  </div>
                </td>
                <td>
                  <span 
                    className="status"
                    style={{ 
                      backgroundColor: client.type === 'commercial' ? '#e3f2fd' : '#f3e5f5',
                      color: client.type === 'commercial' ? '#1976d2' : '#7b1fa2'
                    }}
                  >
                    {client.type}
                  </span>
                </td>
                <td>
                  <strong>{client.totalJobs}</strong>
                </td>
                <td>
                  <strong>{client.totalRevenue}</strong>
                </td>
                <td>{client.lastService}</td>
                <td>
                  <span 
                    className={`status ${client.status === 'active' ? 'status-active' : 'status-pending'}`}
                  >
                    {client.status}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                    <button 
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '11px', 
                        backgroundColor: '#007bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedClient(client)}
                    >
                      View
                    </button>
                    <button 
                      style={{ 
                        padding: '4px 8px', 
                        fontSize: '11px', 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedClient && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#2c3e50', margin: 0 }}>Client Details</h3>
              <button 
                onClick={() => setSelectedClient(null)}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer',
                  color: '#6c757d'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <h4>Contact Information</h4>
                <p><strong>Name:</strong> {selectedClient.name}</p>
                <p><strong>Type:</strong> {selectedClient.type}</p>
                <p><strong>Email:</strong> {selectedClient.email}</p>
                <p><strong>Phone:</strong> {selectedClient.phone}</p>
              </div>
              <div>
                <h4>Service History</h4>
                <p><strong>Total Jobs:</strong> {selectedClient.totalJobs}</p>
                <p><strong>Total Revenue:</strong> {selectedClient.totalRevenue}</p>
                <p><strong>Last Service:</strong> {selectedClient.lastService}</p>
                <p><strong>Status:</strong> {selectedClient.status}</p>
              </div>
            </div>
            
            <div style={{ marginTop: '20px' }}>
              <h4>Address</h4>
              <p>{selectedClient.address}</p>
              <p>{selectedClient.city}, {selectedClient.state} {selectedClient.zipCode}</p>
            </div>
            
            <div style={{ marginTop: '20px' }}>
              <h4>Notes</h4>
              <p>{selectedClient.notes || 'No notes available'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsPage;